/**
 * MHR Sidebar Component
 */

import { Link, useLocation } from 'react-router-dom';
import {
  X,
  LayoutDashboard,
  FolderOpen,
  ListChecks,
  FileText,
  DollarSign,
  Settings,
  ChevronLeft,
  ChevronRight,
  Upload,
  Users,
  LucideIcon,
} from 'lucide-react';
import { useMHR } from '../../contexts';
import { useAuth } from '../../../contexts/AuthContext';

interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
  adminOnly?: boolean;
  superAdminOnly?: boolean;
  comingSoon?: boolean;
}

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export function Sidebar({ isOpen, onClose, isCollapsed, onToggleCollapse }: SidebarProps) {
  const location = useLocation();
  const { isAdmin } = useMHR();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const navItems: NavItem[] = [
    { path: '/mhr', label: 'MedHx Builder', icon: Users },
    { path: '/mhr/protocols', label: 'Protocols', icon: FolderOpen },
    { path: '/mhr/upload', label: 'Upload', icon: Upload },
    { path: '/mhr/reviews', label: 'Reviews', icon: FileText, comingSoon: true },
    { path: '/mhr/budget', label: 'Budget', icon: DollarSign, adminOnly: true },
    { path: '/mhr/settings', label: 'Settings', icon: Settings },
  ];

  const filteredItems = navItems.filter(item => {
    if (item.superAdminOnly) return isSuperAdmin;
    if (item.adminOnly) return isAdmin || isSuperAdmin;
    return true;
  });

  const isActive = (path: string) => {
    if (path === '/mhr') {
      return location.pathname === '/mhr';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50 flex-shrink-0
          bg-white border-r border-gray-200 dark:bg-gray-800 dark:border-gray-700
          transform transition-all duration-200 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${isCollapsed ? 'w-16' : 'w-56'}
        `}
      >
        {/* Mobile close button - only show when expanded */}
        {!isCollapsed && (
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 lg:hidden">
            <span className="font-semibold text-gray-900 dark:text-gray-100">Menu</span>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
          </div>
        )}

        {/* Collapse toggle (desktop only) */}
        <div
          className={`hidden lg:flex items-center border-b border-gray-200 dark:border-gray-700 ${
            isCollapsed ? 'justify-center p-2' : 'justify-end p-2 pr-3'
          }`}
        >
          <button
            onClick={onToggleCollapse}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-gray-700 rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        {/* Navigation */}
        <nav className={`${isCollapsed ? 'p-2' : 'p-3'} space-y-1`}>
          {filteredItems.map(item => {
            const Icon = item.icon;
            const active = isActive(item.path);
            const disabled = item.comingSoon;

            if (disabled) {
              return (
                <div
                  key={item.path}
                  className={`
                    flex items-center rounded-lg opacity-50 cursor-not-allowed text-sm font-medium
                    ${isCollapsed ? 'justify-center p-2.5' : 'gap-3 px-3 py-2.5'}
                    text-gray-400 dark:text-gray-500
                  `}
                  title={isCollapsed ? `${item.label} (Coming Soon)` : 'Coming Soon'}
                >
                  <Icon size={20} />
                  {!isCollapsed && (
                    <span className="flex items-center gap-2">
                      {item.label}
                      <span className="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">Soon</span>
                    </span>
                  )}
                </div>
              );
            }

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={`
                  flex items-center rounded-lg text-sm font-medium transition-colors duration-150
                  ${isCollapsed ? 'justify-center p-2.5' : 'gap-3 px-3 py-2.5'}
                  ${
                    active
                      ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
                  }
                `}
                title={isCollapsed ? item.label : undefined}
              >
                <Icon size={20} className={active ? 'text-primary-600 dark:text-primary-400' : ''} />
                {!isCollapsed && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Help section - only show when expanded */}
        {!isCollapsed && (
          <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-gray-200 dark:border-gray-700">
            <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-3">
              <p className="text-sm font-medium text-primary-900 dark:text-primary-300">MedHx Builder</p>
              <p className="text-xs text-primary-700 dark:text-primary-400 mt-1">
                Build and manage patient medical history logs for eligibility review.
              </p>
            </div>
          </div>
        )}
      </aside>
    </>
  );
}
